/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
import noop from 'lodash/noop';
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import API from '../../api';
import { APIError, resetRequestState } from '../app';

const getSitesForReportsFilters = createAsyncThunk('sites/getSitesForReportsFilters', async (onSuccess = noop) => {
    const response = await API.fetchSites();

    const preparedData = response.map((site) => ({
        label: site.siteName,
        value: site.id,
        ...site,
    }));
    const sortedByName = preparedData.sort((a, b) => a.label.localeCompare(b.label));

    if (onSuccess) onSuccess(sortedByName);
});

const getMunicipalities = createAsyncThunk('sites/getMunicipalities', async () => {
    const municipalities = await API.fetchMunicipalities();
    return municipalities;
});

const fetchSites = createAsyncThunk('sites/getSites', async (params, thunkApi) => {
    const { onSuccess = noop, onError = noop, ...payload } = params || {};
    try {
        const data = await API.fetchSitesList(payload);
        onSuccess();
        return data;
    } catch (error) {
        thunkApi.dispatch(APIError({ text: 'Error fetching sites.' }));
        onError();
        throw error;
    }
});

const createSite = createAsyncThunk(
    'sites/createSite',
    async ({ site, onSuccess = noop, onError = noop }, thunkApi) => {
        try {
            const data = await API.createSite(site);

            onSuccess(data.id);
            return data;
        } catch (error) {
            thunkApi.dispatch(APIError({ text: 'Error creating site.' }));
            onError();
        }
    },
);

const updateSite = createAsyncThunk(
    'sites/updateSite',
    async ({ siteId, site, onSuccess = noop, onError = noop }, thunkApi) => {
        try {
            const result = await API.updateSite(siteId, site);

            onSuccess(siteId);
            return result;
        } catch (error) {
            thunkApi.dispatch(APIError({ text: 'Error updating site.' }));
            onError();
        }
    },
);

const activateSite = createAsyncThunk('sites/activateSite', async ({ siteId, onSuccess = noop, onError = noop }, thunkApi) => {
    try {
        const result = await API.activateSite(siteId);
        onSuccess(siteId);
        return result;
    } catch (error) {
        thunkApi.dispatch(APIError({ text: 'Error activating site.' }));
        onError();
    }
});

const deactivateSite = createAsyncThunk('sites/deactivateSite', async ({ siteId, onSuccess = noop, onError = noop }, thunkApi) => {
    try {
        const result = await API.deactivateSite(siteId);
        onSuccess(siteId);
        return result;
    } catch (error) {
        thunkApi.dispatch(APIError({ text: 'Error deactivating site.' }));
        onError();
    }
});

const initialState = {
    data: [],
    municipalities: [],
    currentRequestId: null,
    errorMsg: null,
    loading: 'idle',
    total: 0,
};

const sitesSlice = createSlice({
    name: 'sites',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(resetRequestState, (state) => {
                state.errorMsg = initialState.errorMsg;
                state.loading = initialState.loading;
                state.currentRequestId = initialState.currentRequestId;
            })
            .addCase(getMunicipalities.fulfilled, (state, action) => {
                state.municipalities = action.payload;
            })
            .addCase(fetchSites.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.loading === 'pending' && state.currentRequestId === requestId) {
                    state.loading = 'idle';
                    state.currentRequestId = undefined;
                    state.data = action.payload.testSites;
                    state.total = action.payload.total;
                }
            })
            .addCase(updateSite.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.loading === 'pending' && state.currentRequestId === requestId) {
                    state.loading = 'idle';
                    state.currentRequestId = undefined;
                    state.data = state.data.map((site) => {
                        if (site.id === action.payload.id) {
                            return action.payload;
                        }
                        return site;
                    });
                }
            })
            .addCase(createSite.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.loading === 'pending' && state.currentRequestId === requestId) {
                    state.loading = 'idle';
                    state.currentRequestId = undefined;
                    state.data = [...state.data, action.payload];
                }
            })
            .addCase(activateSite.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.loading === 'pending' && state.currentRequestId === requestId) {
                    state.loading = 'idle';
                    state.currentRequestId = undefined;
                    state.data = state.data.map((site) => {
                        if (site.id === action.meta.arg.siteId) {
                            return { ...site, deletedAt: null };
                        }
                        return site;
                    });
                }
            })
            .addCase(deactivateSite.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.loading === 'pending' && state.currentRequestId === requestId) {
                    state.loading = 'idle';
                    state.currentRequestId = undefined;
                    state.data = state.data.map((site) => {
                        if (site.id === action.meta.arg.siteId) {
                            return { ...site, deletedAt: new Date().toISOString() };
                        }
                        return site;
                    });
                }
            })
            .addMatcher(
                isAnyOf(updateSite.pending, createSite.pending, fetchSites.pending, activateSite.pending, deactivateSite.pending),
                (state, action) => {
                    if (state.loading === 'idle') {
                        state.loading = 'pending';
                        state.currentRequestId = action.meta.requestId;
                    }
                },
            )
            .addMatcher(
                isAnyOf(createSite.rejected, fetchSites.rejected, updateSite.rejected, activateSite.rejected, deactivateSite.rejected),
                (state, action) => {
                    const { requestId } = action.meta;
                    if (state.loading === 'pending' && state.currentRequestId === requestId) {
                        state.loading = 'idle';
                        state.error = action.error;
                        state.currentRequestId = undefined;
                    }
                },
            );
    },
});

const { reducer } = sitesSlice;

export { getSitesForReportsFilters, getMunicipalities, fetchSites, createSite, updateSite, activateSite, deactivateSite };

export default reducer;
